import type { Group, RuleSet, StandingRow } from '../types.js';
import { rankAcrossGroups, type CrossGroupCandidate } from './standings.js';

/** Tabelle einer Gruppe, wie `phaseStandings` sie liefert */
export interface GroupTable {
  group: Group;
  rows: StandingRow[];
}

export interface PlacedCandidate {
  candidate: CrossGroupCandidate;
  /** Begründung aus `rankAcrossGroups` („Punktdifferenz +12") */
  notes: string[];
  /** Platz in der eigenen Gruppe, nach dem sortiert wurde */
  tier: number;
  group: Group;
}

/**
 * Eine Platzierungsstufe gruppenübergreifend: alle Teams, die in ihrer Gruppe
 * auf Platz `tier` stehen, nach den Kriterien des Regelwerks
 * (`rankAcrossGroups` — ohne direkten Vergleich und Los).
 */
export function placementTier(tables: readonly GroupTable[], rules: RuleSet, tier: number): PlacedCandidate[] {
  const groupOf = new Map(tables.map((tbl) => [tbl.group.id, tbl.group]));
  const cands: CrossGroupCandidate[] = tables.flatMap((tbl) => tbl.rows.filter((r) => r.rank === tier).map((row) => ({ row, groupId: tbl.group.id })));
  return rankAcrossGroups(cands, rules).map(({ candidate, notes }) => ({ candidate, notes, tier, group: groupOf.get(candidate.groupId) as Group }));
}

/**
 * Platzierung vor Punkten (docs/04 §6, docs/notes/ko-runden.md §4): erst alle
 * Gruppenersten, dann alle Zweiten usw. — die Tabelle entscheidet nur
 * **innerhalb** einer Stufe. Ein Zweiter einer schwachen Gruppe steht damit vor
 * jedem Dritten, auch mit weniger Punkten (Harald). Dieselbe Reihenfolge für
 * Setzung ins Bracket, Vorschlagsliste der KO-Runden und Endstand ohne KO.
 */
export function rankByPlacement(tables: readonly GroupTable[], rules: RuleSet): PlacedCandidate[] {
  const maxRank = Math.max(0, ...tables.flatMap((tbl) => tbl.rows.map((r) => r.rank)));
  const out: PlacedCandidate[] = [];
  for (let tier = 1; tier <= maxRank; tier++) out.push(...placementTier(tables, rules, tier));
  return out;
}
